<script setup>
import { useMainStore } from '@/store'
import { computed, onMounted, ref, watch } from 'vue'
import { addLateDeliverySurcharge } from '@/utils/helper'

// stores
const store = useMainStore()

// passed properties
const props = defineProps({
  article: {
    type: Object,
    required: true
  },
  refinement: {
    type: Object,
    required: true
  },
})

// emits
const emit = defineEmits(['remove', 'update', 'add'])

// computed values
const inEnglish = computed(() => store.inEnglish)
const basket = computed(() => store.settings.basket)
const shoppingCart = computed(() => store.settings.shopping_cart)
const printArticle = computed(() => {
  const index = shoppingCart.value.print_items.findIndex((article) => {
    return article.id === props.article.article.id && article.booth_refinement_id === props.refinement.id
  })

  if (index > -1) {
    return shoppingCart.value.print_items[index]
  }
  return null
})

const printArticleCount = ref(props.article.count)
watch(() => printArticle.value?.count, (val) => 
  printArticleCount.value = val ?? 0
)

// Load selected count on mounted
onMounted(() => {
  if (printArticle.value) printArticleCount.value = printArticle.value.count ?? 0
})

const letterArticle = computed(() => {
  const index = basket.value.selectedArticles.findIndex((article) => {
    return article.id === props.article.article.id
  })
  if (index === -1) {
    console.warn('lettering article not found', {article_id: props.article.id})
    return null
  }
  return basket.value.selectedArticles[index]
})
const hasImage = computed(() => {
  if (props.article.article.images === null) {
    return false
  }
  return props.article.article.images.length > 0
})
const previewImage = computed(() => {
  if (!hasImage.value) {
    return ARTICLE_DEFAULT_IMAGE
  }
  return '/images/articles/' + props.article.article.images[0].path;
})
const displayName = computed(() => {
  return (inEnglish.value) ? props.article.article.name_en : props.article.article.name
})
const displayDescription = computed(() => {
  return (inEnglish.value) ? props.article.article.description_en : props.article.article.description
})
const earlyBirdMode = computed(() => store.tmp.early_bird_mode)
const lateDelivery = computed(() => store.tmp.late_delivery)
const price = computed(() => {
  if (letterArticle.value === null) {
    return 0
  }
  
  return (earlyBirdMode.value)
    ? addLateDeliverySurcharge(letterArticle.value.exhibitionPrices.price_net, lateDelivery.value)
    : letterArticle.value.exhibitionPrices.price_net
})

// methods
const removeFromBasket = () => {
  emit('remove', props.refinement, props.article)
}

const addToBasket = () => {
  emit('add', props.refinement, props.article)
}

const updateCount = () => {
  emit('update', props.refinement, props.article, printArticleCount.value)
}
</script>

<template>
  <div class="article-inner-frame">
    <div class="article-thumb article-img-slider col-xs-4">
      <div v-if="hasImage" class="article-img-frame" style="width: 100%;">
        <img :src="previewImage" alt="" class="pc-print__image">
      </div>
      <div class="article-img-frame" v-else></div>
    </div>
    <div class="article-info col-xs-8">
      <span class="article-label">{{ displayName }}</span>
      <article class="article-description">{{ displayDescription }}</article>
      <div class="action-row row">
        <span class="article-price col-xs-5">
          {{ $filters.formatMoney(price) }}
          <small class="article-unit" :class="article.article.unit">
            <span v-if="article.article.unit === 'sqm'" class="square_m">
              /m<sup>2</sup>
            </span>
            <span v-else-if="article.article.unit === 'pc'" class="per_unit">
              {{ $t("booth_articles.per_unit") }}
            </span>
          </small>
          <small v-if="letterArticle && letterArticle.inclusive">
            ({{ letterArticle.inclusive}} {{ $t('labels.inclusive_abbreviation') }})
          </small>
        </span>
      </div>
      <div class="action-row row">
        <div class="input-group input-group-sm col-xs-6 pull-right">
          <span class="input-group-btn">
            <button
              class="btn btn-primary btn-sm"
              tabindex="-1"
              type="button"
              :disabled="printArticle === null || printArticle.count < 1"
              @click="removeFromBasket"
            >
              <i class="fa fa-minus" aria-hidden="true"></i>
            </button>
          </span>
          <input
            class="form-control"
            type="number"
            :step="1"
            :min="0"
            name="depth"
            v-model="printArticleCount"
            :placeholder="0"
            @blur="updateCount"
          >
          <span class="input-group-btn">
            <button
              class="btn btn-primary btn-sm"
              tabindex="-1"
              type="button"
              @click="addToBasket"
            >
              <i class="fa fa-plus" aria-hidden="true"></i>
            </button>
          </span>
          <span class="input-group-btn" style="font-size:14px;">
            <small class="article-unit" v-if="article.article.unit === 'sqm'">m<sup>2</sup></small>
            <small class="article-unit" v-if="article.article.unit === 'pc'">&nbsp;&nbsp;</small>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>